import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import axios from 'axios';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Alert, Button, Chip, Snackbar, Stack, Switch as MuiSwitch, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { Redirect, Route, Switch as RouterSwitch, useHistory, useParams } from 'react-router-dom';
import { IVerbaScholaAttemptSoundGrade, IVerbaScholaSpeakingReviewAttempt, IVerbaScholaStudentProgress, lessonAuthoringError, lessonPublicationError, VerbaScholaApi } from '../api/VerbaScholaApi';
import VerbaScholaTeacherShell from '../component/VerbaScholaTeacherShell';
import useVerbaScholaResource from '../hook/useVerbaScholaResource';
import { BLUE, BLUE_PRESSED, BORDER, ERROR_SOFT, ERROR_TEXT, GREEN, INTER, SLATE, SUCCESS_SOFT, SUCCESS_TEXT, WARNING_SOFT, WARNING_TEXT } from '../style/verbaScholaStyles';
import VerbaScholaTeacherLessonOverviewPage from './VerbaScholaTeacherLessonOverviewPage';
import VerbaScholaTeacherLessonOpenResponseReviewPage from './VerbaScholaTeacherLessonOpenResponseReviewPage';
import VerbaScholaTeacherLessonSpeakingReviewPage from './VerbaScholaTeacherLessonSpeakingReviewPage';
import VerbaScholaTeacherLessonStudentsPage from './VerbaScholaTeacherLessonStudentsPage';

type LessonTab = 'overview' | 'student' | 'speaking-review' | 'open-response-review';
const TAB_LIST: { value: LessonTab; label: string }[] = [
    { value: 'overview', label: 'Overview' },
    { value: 'student', label: 'Students' },
    { value: 'speaking-review', label: 'Speaking review' },
    { value: 'open-response-review', label: 'Open answer review' },
];

function statusChipSx(status: string) {
    const positive = status === 'published' || status === 'current' || status === 'complete' || status === 'ready';
    return {
        fontFamily: INTER,
        fontWeight: 700,
        fontSize: 11,
        textTransform: 'capitalize',
        color: positive ? SUCCESS_TEXT : WARNING_TEXT,
        backgroundColor: positive ? SUCCESS_SOFT : WARNING_SOFT,
    };
}

function audioChipSx(allReady: boolean) {
    return {
        ...statusChipSx('ready'),
        color: allReady ? SUCCESS_TEXT : ERROR_TEXT,
        backgroundColor: allReady ? SUCCESS_SOFT : ERROR_SOFT,
        '& .MuiChip-icon': { color: GREEN, fontSize: 18 },
    };
}

interface VerbaScholaTeacherLessonWorkspaceProps {
    courseId: number;
    lesson: number;
    activeTab: LessonTab;
}

export default function VerbaScholaTeacherLessonPage() {
    const { courseId: courseIdParam, lessonId, lessonTab } = useParams() as { courseId: string; lessonId: string; lessonTab?: string };
    const courseId = Number(courseIdParam);
    const lesson = Number(lessonId);
    const activeTab = TAB_LIST.find(tab => tab.value === lessonTab)?.value;

    if (!activeTab) return <Redirect to={`/teacher/course/${courseId}/lesson/${lesson}/overview`} />;
    return <VerbaScholaTeacherLessonWorkspace key={`${courseId}:${lesson}`} courseId={courseId} lesson={lesson} activeTab={activeTab} />;
}

function VerbaScholaTeacherLessonWorkspace({ courseId, lesson, activeTab }: VerbaScholaTeacherLessonWorkspaceProps) {
    const history = useHistory();
    const theme = useTheme();
    const phoneHeader = useMediaQuery(theme.breakpoints.down('sm'));
    const [message, setMessage] = React.useState<string | null>(null);
    const [pictureReviewNeeded, setPictureReviewNeeded] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const showMessage = (value: string | null) => { setPictureReviewNeeded(false); setMessage(value); };

    const overview = useVerbaScholaResource(
        () => VerbaScholaApi.getLessonOverview(lesson),
        [lesson],
        'That lesson does not exist.',
    );
    const studentList = useVerbaScholaResource<IVerbaScholaStudentProgress[]>(
        () => VerbaScholaApi.getLessonStudentList(lesson),
        [lesson],
        'That lesson does not exist.',
    );
    const speakingReview = useVerbaScholaResource<IVerbaScholaSpeakingReviewAttempt[]>(
        () => VerbaScholaApi.getLessonSpeakingReview(lesson),
        [lesson],
        'That lesson does not exist.',
    );
    const course = useVerbaScholaResource(
        () => VerbaScholaApi.getCourse(courseId),
        [courseId],
        'That class does not exist.',
    );
    const audioReadiness = useVerbaScholaResource(
        () => VerbaScholaApi.getLessonAudioReadiness(lesson),
        [lesson],
        'That lesson does not exist.',
    );
    const audioTotal = audioReadiness.data?.total_sentences;
    const audioAllReady = audioReadiness.data?.all_ready;
    const audioError = audioReadiness.error;
    const reloadAudioReadiness = audioReadiness.reload;

    React.useEffect(() => {
        if (!audioTotal || audioAllReady || audioError) return;
        const refresh = () => {
            if (document.visibilityState === 'visible') reloadAudioReadiness();
        };
        const interval = window.setInterval(refresh, 30000);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [audioTotal, audioAllReady, audioError, reloadAudioReadiness]);

    const active = activeTab === 'student' ? studentList : activeTab === 'speaking-review' ? speakingReview : overview;
    const lessonRecord = overview.data?.lesson;
    const courseRecord = course.data;
    const exercises = overview.data?.exercises ?? [];
    const speakingAttempts = speakingReview.data ?? [];
    const awaitingReviewCount = speakingAttempts.filter(attempt => !attempt.feedback_sent_at).length;
    const isPublished = !lessonRecord?.is_hidden && (lessonRecord?.status === 'published' || lessonRecord?.status === 'current' || lessonRecord?.status === 'complete');

    function updateLessonVisibility(available: boolean) {
        if (!lessonRecord || lessonRecord.is_archived || courseRecord?.is_archived) return;
        if (!available && !window.confirm('Hide this lesson from students? Their saved work will remain, but they cannot open the lesson until you make it available again.')) return;
        setSaving(true);
        VerbaScholaApi.updateLesson(lesson, { status: available && lessonRecord.status === 'draft' ? 'published' : lessonRecord.status, is_hidden: !available,
            ...(available ? { require_playable_exercises: true } : {}),
            expected_authoring_revision: lessonRecord.authoring_revision })
            .then(() => {
                setPictureReviewNeeded(false);
                showMessage(available ? 'Lesson is now available to students.' : 'Lesson is now hidden from students. Saved work remains.');
                overview.reload();
            })
            .catch(error => {
                setPictureReviewNeeded(axios.isAxiosError(error) && error.response?.data?.message === 'lesson_pictures_need_review');
                setMessage(lessonPublicationError(error) ?? 'Could not save changes.');
            })
            .finally(() => setSaving(false));
    }

    function unpublishLesson() {
        if (!lessonRecord || lessonRecord.status === 'draft' ||
            !window.confirm('Unpublish this lesson and return it to draft? Students will lose access, but their saved work will remain.')) return;
        setSaving(true);
        VerbaScholaApi.updateLesson(lesson, { status: 'draft', is_hidden: true,
            expected_authoring_revision: lessonRecord.authoring_revision })
            .then(() => { showMessage('Lesson returned to draft. Student work is preserved.'); overview.reload(); })
            .catch(error => showMessage(lessonPublicationError(error) ?? 'Could not unpublish this lesson.'))
            .finally(() => setSaving(false));
    }

    function archiveLesson() {
        if (!lessonRecord || lessonRecord.is_archived || courseRecord?.is_archived ||
            !window.confirm('Archive this lesson? It will be hidden from students and kept with its saved student work. Any unfinished AI work on it will close.')) return;
        setSaving(true);
        VerbaScholaApi.updateLesson(lesson, { is_archived: true, is_hidden: true,
            expected_authoring_revision: lessonRecord.authoring_revision })
            .then(() => { showMessage('Lesson archived. Saved student work remains.'); overview.reload(); })
            .catch(error => showMessage(lessonPublicationError(error) ?? 'Could not archive this lesson.'))
            .finally(() => setSaving(false));
    }

    function unarchiveLesson() {
        if (!lessonRecord?.is_archived || courseRecord?.is_archived || saving) return;
        setSaving(true);
        VerbaScholaApi.updateLesson(lesson, { is_archived: false, is_hidden: true,
            expected_authoring_revision: lessonRecord.authoring_revision })
            .then(() => { showMessage('Lesson restored to the class, still hidden from students.'); overview.reload(); })
            .catch(error => showMessage(lessonPublicationError(error) ?? 'Could not unarchive this lesson.'))
            .finally(() => setSaving(false));
    }

    function duplicateLessonToDraft() {
        if (!lessonRecord) return;
        setSaving(true);
        VerbaScholaApi.duplicateLessonToDraft(lesson, lessonRecord.authoring_revision)
            .then(response => history.push(`/teacher/course/${courseId}/lesson/${response.data.id}/description`))
            .catch(error => showMessage(lessonAuthoringError(error, 'Could not create a draft copy. Refresh the lesson and try again.')))
            .finally(() => setSaving(false));
    }

    function saveSpeakingFeedback(attemptId: number, feedback: string, soundGrades?: IVerbaScholaAttemptSoundGrade[]) {
        setSaving(true);
        return VerbaScholaApi.saveTeacherSpeakingReview(attemptId, {
            teacher_feedback: feedback.trim().length === 0 ? null : feedback.trim(),
            send_feedback: true,
            ...(soundGrades === undefined ? {} : { sound_grades: soundGrades }),
        })
            .then(response => {
                showMessage('Feedback sent to the student.');
                speakingReview.reload();
                return response.data.sound_grades;
            })
            .catch(() => {
                showMessage('Could not save changes.');
                throw new Error('Could not save speaking review.');
            })
            .finally(() => setSaving(false));
    }

    const tabLabel = (tab: { value: LessonTab; label: string }) => tab.value === 'speaking-review' && awaitingReviewCount > 0
        ? `${tab.label} · ${awaitingReviewCount}`
        : tab.label;

    const lessonEditable = Boolean(lessonRecord) && !lessonRecord?.is_archived && !courseRecord?.is_archived;
    const canDuplicate = lessonEditable && ['published', 'current', 'complete'].includes(lessonRecord?.status ?? '');
    const hasLifecycleAction = lessonEditable || Boolean(lessonRecord?.is_archived && !courseRecord?.is_archived);
    // Student preview shares a row with Duplicate as draft, above the lifecycle buttons and Edit lesson. Without
    // that partner it leads the last row, except on phones with Archive or Unarchive, where that row has no room
    // and it sits at the right end of the status row instead.
    const previewInStatusRow = phoneHeader && !canDuplicate && hasLifecycleAction;
    const previewButton = <Button startIcon={<VisibilityRoundedIcon />} disabled={exercises.length === 0 || lessonRecord?.is_archived || courseRecord?.is_archived} onClick={() => history.push(`/teacher/course/${courseId}/lesson/${lesson}/exercises?preview=1`)} sx={{ flexShrink: 0, color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Student preview</Button>;
    // Phones use shorter visible labels so Unpublish, Archive and Edit lesson fit one row from 360px.
    const lifecycleButtons = (
        <>
            {lessonEditable && lessonRecord?.status !== 'draft' && <Button aria-label="Unpublish lesson" disabled={saving} onClick={unpublishLesson}
                sx={{ color: ERROR_TEXT, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>{phoneHeader ? 'Unpublish' : 'Unpublish lesson'}</Button>}
            {lessonEditable && <Button aria-label="Archive lesson" startIcon={phoneHeader ? undefined : <ArchiveOutlinedIcon />} disabled={saving} onClick={archiveLesson}
                sx={{ color: ERROR_TEXT, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>{phoneHeader ? 'Archive' : 'Archive lesson'}</Button>}
            {lessonRecord?.is_archived && !courseRecord?.is_archived && <Button disabled={saving} onClick={unarchiveLesson}
                sx={{ color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Unarchive lesson</Button>}
        </>
    );
    const editButton = (
        <Button
            variant="contained"
            startIcon={<EditRoundedIcon />}
            onClick={() => history.push(`/teacher/course/${courseId}/lesson/${lesson}/description`)}
            sx={{ fontFamily: INTER, fontWeight: 700, textTransform: 'none', fontSize: 15, backgroundColor: BLUE, color: '#fff', borderRadius: '11px', px: 2.25, boxShadow: 'none', '&:hover': { backgroundColor: BLUE_PRESSED, boxShadow: 'none' } }}
        >
            Edit lesson
        </Button>
    );
    const publicationStatus = lessonRecord && (
        <>
            {!lessonRecord.is_archived && lessonRecord.status !== 'draft' && <Chip label={lessonRecord.is_hidden ? 'Published · hidden' : lessonRecord.status} size="small" sx={statusChipSx(lessonRecord.is_hidden ? 'draft' : lessonRecord.status)} />}
            {lessonRecord.is_archived && <Chip label="Archived" size="small" sx={statusChipSx('draft')} />}
            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ pl: 1, pr: 0.25, minHeight: 30, border: `1px solid ${BORDER}`, borderRadius: '999px', backgroundColor: '#fff' }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: isPublished ? SUCCESS_TEXT : WARNING_TEXT }}>{lessonRecord.is_archived ? 'Archived lesson' : isPublished ? 'Available to students' : lessonRecord.status === 'draft' ? 'Unpublished draft' : 'Hidden from students'}</Typography>
                <MuiSwitch size="small" checked={isPublished} disabled={saving || lessonRecord.is_archived || courseRecord?.is_archived} onChange={event => updateLessonVisibility(event.target.checked)} inputProps={{ 'aria-label': 'Lesson available to students' }} />
            </Stack>
        </>
    );
    const audioStatus = audioReadiness.error ? (
        <Chip size="small" label="Audio status unavailable · retry" onClick={audioReadiness.reload}
            aria-label="Audio status unavailable. Retry loading audio status." sx={statusChipSx('draft')} />
    ) : audioReadiness.data && audioReadiness.data.total_sentences > 0 ? (
        <Chip size="small" icon={audioReadiness.data.all_ready ? <CheckCircleRoundedIcon /> : undefined}
            label={`Student audio ${audioReadiness.data.available}/${audioReadiness.data.total_sentences} ready`}
            title="Audio for ready, student-facing activities. Draft and hidden exercises are excluded."
            aria-label={audioReadiness.data.all_ready ? `All ${audioReadiness.data.total_sentences} student activity audio sentences ready` : `Student activity audio incomplete: ${audioReadiness.data.available} of ${audioReadiness.data.total_sentences} ready`}
            sx={audioChipSx(audioReadiness.data.all_ready)} />
    ) : null;
    // On phones, Student preview stays beside the publication toggle and audio status moves to its own row,
    // so a wide audio chip cannot push the preview onto a line of its own.
    const statusRow = !lessonRecord ? null : previewInStatusRow ? (
        <Stack spacing={1}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0, minHeight: '36.5px' }}>
                    {publicationStatus}
                </Stack>
                {previewButton}
            </Stack>
            {audioStatus && <Stack direction="row">{audioStatus}</Stack>}
        </Stack>
    ) : (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            {publicationStatus}
            {audioStatus}
        </Stack>
    );
    return (
        <VerbaScholaTeacherShell
            title={lessonRecord?.title ?? 'Lesson'}
            contextLabel={
                courseRecord ? (
                    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.4 }}>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: BLUE, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{courseRecord.name}</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>· Lesson</Typography>
                    </Stack>
                ) : null
            }
            titleAdornment={statusRow}
            titleAdornmentFullWidth={Boolean(lessonRecord)}
            headerVariant="workspace"
            backTo={`/teacher/course/${courseId}/lesson`}
            backLabel="Class lessons"
            loading={(active.loading && active.data == null) || (overview.loading && overview.data == null) || (course.loading && course.data == null)}
            refreshing={(active.loading && active.data != null) || (overview.loading && overview.data != null) || (course.loading && course.data != null)}
            error={active.error ?? overview.error ?? course.error}
            onRetry={active.reload}
            actions={
                <Stack spacing={1}>
                    {canDuplicate && (
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} flexWrap="wrap" useFlexGap>
                            {previewButton}
                            <Button startIcon={<ContentCopyRoundedIcon />} disabled={saving} onClick={duplicateLessonToDraft} sx={{ color: BLUE, fontFamily: INTER, fontWeight: 700, textTransform: 'none' }}>Duplicate as draft</Button>
                        </Stack>
                    )}
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} flexWrap="wrap" useFlexGap>
                        {!canDuplicate && !previewInStatusRow && previewButton}
                        {lifecycleButtons}
                        {editButton}
                    </Stack>
                </Stack>
            }
            navigation={
                <Tabs
                    value={activeTab}
                    onChange={(_event, value: LessonTab) => history.push(`/teacher/course/${courseId}/lesson/${lesson}/${value}`)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mt: 2.5, minHeight: 44, '& .MuiTab-root': { minHeight: 44, minWidth: 0, mr: { xs: 2.25, sm: 3.5 }, px: 0, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, textTransform: 'none', color: SLATE }, '& .Mui-selected': { color: `${BLUE} !important` }, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: BLUE } }}
                >
                    {TAB_LIST.map(tab => <Tab key={tab.value} value={tab.value} label={tabLabel(tab)} />)}
                </Tabs>
            }
        >

            <RouterSwitch>
                <Route
                    exact
                    path="/teacher/course/:courseId/lesson/:lessonId/overview"
                    render={() => (
                        <VerbaScholaTeacherLessonOverviewPage
                            students={overview.data?.students ?? []}
                            exercises={exercises}
                            speakingAttempts={speakingAttempts}
                            onReviewSpeaking={() => history.push(`/teacher/course/${courseId}/lesson/${lesson}/speaking-review`)}
                            onViewStudents={() => history.push(`/teacher/course/${courseId}/lesson/${lesson}/student`)}
                        />
                    )}
                />
                <Route
                    exact
                    path="/teacher/course/:courseId/lesson/:lessonId/student"
                    render={() => (
                        <VerbaScholaTeacherLessonStudentsPage
                            students={studentList.data ?? []}
                            speakingAttempts={speakingAttempts}
                            onOpenSpeakingReview={attemptId => history.push(`/teacher/course/${courseId}/lesson/${lesson}/speaking-review${attemptId ? `?attempt=${attemptId}` : ''}`)}
                        />
                    )}
                />
                <Route
                    exact
                    path="/teacher/course/:courseId/lesson/:lessonId/speaking-review"
                    render={() => <VerbaScholaTeacherLessonSpeakingReviewPage attempts={speakingAttempts} saving={saving} onSave={saveSpeakingFeedback} onAction={setMessage} />}
                />
                <Route
                    exact
                    path="/teacher/course/:courseId/lesson/:lessonId/open-response-review"
                    render={() => <VerbaScholaTeacherLessonOpenResponseReviewPage lessonId={lesson} />}
                />
                <Redirect to={`/teacher/course/${courseId}/lesson/${lesson}/overview`} />
            </RouterSwitch>

            <Snackbar open={message !== null} autoHideDuration={pictureReviewNeeded ? null : 3000} onClose={() => showMessage(null)}>
                <Alert severity={pictureReviewNeeded || message?.startsWith('Could not') ? 'error' : 'success'} onClose={() => showMessage(null)}
                    action={pictureReviewNeeded ? <Button color="inherit" onClick={() => history.push(`/teacher/course/${courseId}/lesson/${lesson}/exercises`)}>Review exercises</Button> : undefined}
                    sx={{ fontFamily: INTER }}>
                    {message}
                </Alert>
            </Snackbar>
        </VerbaScholaTeacherShell>
    );
}
